import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Search,
  Plus,
  Edit,
  Trash2,
  DollarSign,
  Calendar,
  Phone,
  Mail,
  MapPin,
  Briefcase,
  X,
  Save,
  Download,
  Filter,
  ChevronDown,
  ChevronUp,
  UserPlus,
  CreditCard,
  TrendingUp,
  Clock,
} from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import {
  getAllStaff,
  addStaff,
  updateStaff,
  deleteStaff,
  getStaffSalaries,
  addSalaryPayment,
  getAllSalaryPayments,
} from '../../services/staffService';

const StaffManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState([]);
  const [salaryPayments, setSalaryPayments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);

  // Modal states
  const [showAddStaffModal, setShowAddStaffModal] = useState(false);
  const [showEditStaffModal, setShowEditStaffModal] = useState(false);
  const [showSalaryModal, setShowSalaryModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);

  // Form states
  const [staffForm, setStaffForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    department: '',
    position: '',
    joiningDate: format(new Date(), 'yyyy-MM-dd'),
    salary: '',
    status: 'active',
    // Bank Details
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    accountHolderName: '',
    panNumber: '',
  });

  const [salaryForm, setSalaryForm] = useState({
    staffId: '',
    staffName: '',
    month: format(new Date(), 'yyyy-MM'),
    basicSalary: '',
    allowances: '',
    deductions: '',
    bonus: '',
    paymentDate: format(new Date(), 'yyyy-MM-dd'),
    paymentMethod: 'bank_transfer',
    notes: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [staffData, salaryData] = await Promise.all([
        getAllStaff(),
        getAllSalaryPayments(),
      ]);
      setStaff(staffData);
      setSalaryPayments(salaryData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load staff data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics
  const stats = {
    totalStaff: staff.length,
    activeStaff: staff.filter(s => s.status === 'active').length,
    totalSalaryExpense: staff
      .filter(s => s.status === 'active')
      .reduce((sum, s) => sum + (parseFloat(s.salary) || 0), 0),
    recentPayments: salaryPayments.filter(p => {
      const paymentDate = new Date(p.paymentDate);
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return paymentDate >= thirtyDaysAgo;
    }).length,
  };

  // Filter staff
  const filteredStaff = staff.filter(member => {
    const matchesSearch =
      member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.phone?.includes(searchTerm) ||
      member.department?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === 'all' || member.status === filterStatus;
    const matchesDepartment =
      filterDepartment === 'all' || member.department === filterDepartment;

    return matchesSearch && matchesStatus && matchesDepartment;
  });

  // Get unique departments
  const departments = [...new Set(staff.map(s => s.department).filter(Boolean))];

  // Handle Add Staff
  const handleAddStaff = async () => {
    try {
      if (!staffForm.name || !staffForm.email || !staffForm.phone || !staffForm.salary) {
        toast.error('Please fill in all required fields');
        return;
      }

      await addStaff(staffForm);
      toast.success('Staff member added successfully');
      setShowAddStaffModal(false);
      resetStaffForm();
      loadData();
    } catch (error) {
      console.error('Error adding staff:', error);
      toast.error('Failed to add staff member');
    }
  };

  // Handle Edit Staff
  const handleEditStaff = async () => {
    try {
      if (!selectedStaff) return;

      await updateStaff(selectedStaff.id, staffForm);
      toast.success('Staff member updated successfully');
      setShowEditStaffModal(false);
      setSelectedStaff(null);
      resetStaffForm();
      loadData();
    } catch (error) {
      console.error('Error updating staff:', error);
      toast.error('Failed to update staff member');
    }
  };

  // Handle Delete Staff
  const handleDeleteStaff = async () => {
    try {
      if (!selectedStaff) return;

      await deleteStaff(selectedStaff.id);
      toast.success('Staff member deleted successfully');
      setShowDeleteModal(false);
      setSelectedStaff(null);
      loadData();
    } catch (error) {
      console.error('Error deleting staff:', error);
      toast.error('Failed to delete staff member');
    }
  };

  // Handle Add Salary Payment
  const handleAddSalaryPayment = async () => {
    try {
      const basicSalary = parseFloat(salaryForm.basicSalary) || 0;
      const allowances = parseFloat(salaryForm.allowances) || 0;
      const deductions = parseFloat(salaryForm.deductions) || 0;
      const bonus = parseFloat(salaryForm.bonus) || 0;

      const netSalary = basicSalary + allowances + bonus - deductions;

      const salaryData = {
        ...salaryForm,
        basicSalary,
        allowances,
        deductions,
        bonus,
        netSalary,
      };

      await addSalaryPayment(salaryData);
      toast.success('Salary payment recorded successfully');
      setShowSalaryModal(false);
      resetSalaryForm();
      loadData();
    } catch (error) {
      console.error('Error adding salary payment:', error);
      toast.error('Failed to record salary payment');
    }
  };

  const openEditModal = (member) => {
    setSelectedStaff(member);
    setStaffForm({
      name: member.name || '',
      email: member.email || '',
      phone: member.phone || '',
      address: member.address || '',
      department: member.department || '',
      position: member.position || '',
      joiningDate: member.joiningDate || format(new Date(), 'yyyy-MM-dd'),
      salary: member.salary || '',
      status: member.status || 'active',
      // Bank Details
      bankName: member.bankName || '',
      accountNumber: member.accountNumber || '',
      ifscCode: member.ifscCode || '',
      accountHolderName: member.accountHolderName || '',
      panNumber: member.panNumber || '',
    });
    setShowEditStaffModal(true);
  };

  const openSalaryModal = (member) => {
    setSelectedStaff(member);
    setSalaryForm({
      staffId: member.id,
      staffName: member.name,
      month: format(new Date(), 'yyyy-MM'),
      basicSalary: member.salary || '',
      allowances: '',
      deductions: '',
      bonus: '',
      paymentDate: format(new Date(), 'yyyy-MM-dd'),
      paymentMethod: 'bank_transfer',
      notes: '',
    });
    setShowSalaryModal(true);
  };

  const openDeleteModal = (member) => {
    setSelectedStaff(member);
    setShowDeleteModal(true);
  };

  const resetStaffForm = () => {
    setStaffForm({
      name: '',
      email: '',
      phone: '',
      address: '',
      department: '',
      position: '',
      joiningDate: format(new Date(), 'yyyy-MM-dd'),
      salary: '',
      status: 'active',
      // Bank Details
      bankName: '',
      accountNumber: '',
      ifscCode: '',
      accountHolderName: '',
      panNumber: '',
    });
  };

  const resetSalaryForm = () => {
    setSalaryForm({
      staffId: '',
      staffName: '',
      month: format(new Date(), 'yyyy-MM'),
      basicSalary: '',
      allowances: '',
      deductions: '',
      bonus: '',
      paymentDate: format(new Date(), 'yyyy-MM-dd'),
      paymentMethod: 'bank_transfer',
      notes: '',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading staff data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-full mx-auto px-4 py-5 md:px-6 md:py-6 bg-gray-50 min-h-screen pb-52">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-[18px] md:text-2xl lg:text-3xl font-bold text-gray-900">
              Staff Management
            </h2>
            <p className="text-[11px] md:text-sm text-gray-600 mt-1">
              Manage your team members and salary payments
            </p>
          </div>
          <button
            onClick={() => setShowAddStaffModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-neutral-900 hover:bg-neutral-800 text-white rounded-xl transition-all shadow-lg font-medium"
          >
            <UserPlus className="w-4 h-4" />
            <span>Add Staff</span>
          </button>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            title="Total Staff"
            value={stats.totalStaff}
            icon={Users}
            color="blue"
          />
          <StatCard
            title="Active Staff"
            value={stats.activeStaff}
            icon={TrendingUp}
            color="green"
          />
          <StatCard
            title="Monthly Expense"
            value={`₹${stats.totalSalaryExpense.toLocaleString()}`}
            icon={DollarSign}
            color="purple"
          />
          <StatCard
            title="Recent Payments"
            value={stats.recentPayments}
            icon={CreditCard}
            color="orange"
          />
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <button
            onClick={() => setIsFilterExpanded(!isFilterExpanded)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filters & Search</span>
            </div>
            <div className="md:hidden">
              {isFilterExpanded ? (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </div>
          </button>

          <div
            className={`transition-all duration-300 ease-in-out overflow-hidden ${
              isFilterExpanded ? 'max-h-96 md:max-h-none' : 'max-h-0 md:max-h-none'
            }`}
          >
            <div className="p-4 border-t border-gray-100 space-y-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by name, email, phone, or department..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                />
              </div>

              {/* Filter Options */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="on_leave">On Leave</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Department
                  </label>
                  <select
                    value={filterDepartment}
                    onChange={(e) => setFilterDepartment(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  >
                    <option value="all">All Departments</option>
                    {departments.map((dept) => (
                      <option key={dept} value={dept}>
                        {dept}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Staff List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Staff Member
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Department
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Salary
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredStaff.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                    No staff members found
                  </td>
                </tr>
              ) : (
                filteredStaff.map((member) => (
                  <tr key={member.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4">
                      <div>
                        <p className="font-semibold text-gray-900">{member.name}</p>
                        <p className="text-sm text-gray-500">{member.position}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Mail className="w-3 h-3" />
                          <span className="truncate max-w-[200px]">{member.email}</span>
                        </div>
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Phone className="w-3 h-3" />
                          <span>{member.phone}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs font-medium">
                        {member.department}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <p className="font-semibold text-gray-900">
                        ₹{parseFloat(member.salary || 0).toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500">per month</p>
                    </td>
                    <td className="px-4 py-4">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          member.status === 'active'
                            ? 'bg-green-100 text-green-700'
                            : member.status === 'inactive'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}
                      >
                        {member.status}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openSalaryModal(member)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Pay Salary"
                        >
                          <DollarSign className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openEditModal(member)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openDeleteModal(member)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Staff Modal */}
      {showAddStaffModal && (
        <Modal
          title="Add New Staff Member"
          onClose={() => {
            setShowAddStaffModal(false);
            resetStaffForm();
          }}
        >
          <StaffForm
            form={staffForm}
            setForm={setStaffForm}
            onSubmit={handleAddStaff}
            onCancel={() => {
              setShowAddStaffModal(false);
              resetStaffForm();
            }}
            submitLabel="Add Staff"
          />
        </Modal>
      )}

      {/* Edit Staff Modal */}
      {showEditStaffModal && (
        <Modal
          title="Edit Staff Member"
          onClose={() => {
            setShowEditStaffModal(false);
            setSelectedStaff(null);
            resetStaffForm();
          }}
        >
          <StaffForm
            form={staffForm}
            setForm={setStaffForm}
            onSubmit={handleEditStaff}
            onCancel={() => {
              setShowEditStaffModal(false);
              setSelectedStaff(null);
              resetStaffForm();
            }}
            submitLabel="Update Staff"
          />
        </Modal>
      )}

      {/* Salary Payment Modal */}
      {showSalaryModal && (
        <Modal
          title="Record Salary Payment"
          onClose={() => {
            setShowSalaryModal(false);
            setSelectedStaff(null);
            resetSalaryForm();
          }}
        >
          <SalaryForm
            form={salaryForm}
            setForm={setSalaryForm}
            onSubmit={handleAddSalaryPayment}
            onCancel={() => {
              setShowSalaryModal(false);
              setSelectedStaff(null);
              resetSalaryForm();
            }}
          />
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <Modal
          title="Delete Staff Member"
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedStaff(null);
          }}
        >
          <div className="p-6">
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete <strong>{selectedStaff?.name}</strong>? This
              action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedStaff(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteStaff}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
  };

  return (
    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
      <div className="flex items-center gap-3">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-xs text-gray-500 font-medium uppercase">{title}</p>
          <p className="text-xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  );
};

// Modal Component
const Modal = ({ title, children, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h3 className="text-xl font-bold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

// Staff Form Component
const StaffForm = ({ form, setForm, onSubmit, onCancel, submitLabel }) => {
  return (
    <div className="p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
            placeholder="Enter full name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
            placeholder="email@example.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Phone <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
            placeholder="Enter phone number"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Department
          </label>
          <input
            type="text"
            value={form.department}
            onChange={(e) => setForm({ ...form, department: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
            placeholder="e.g., Sales, IT, HR"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Position
          </label>
          <input
            type="text"
            value={form.position}
            onChange={(e) => setForm({ ...form, position: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
            placeholder="e.g., Manager, Developer"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Joining Date
          </label>
          <input
            type="date"
            value={form.joiningDate}
            onChange={(e) => setForm({ ...form, joiningDate: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Monthly Salary (₹) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            value={form.salary}
            onChange={(e) => setForm({ ...form, salary: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
            placeholder="Enter monthly salary"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="on_leave">On Leave</option>
          </select>
        </div>


        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
          <textarea
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            rows="3"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
            placeholder="Enter full address"
          />
        </div>

        {/* Bank Details Section */}
        <div className="md:col-span-2">
          <div className="border-t border-gray-200 pt-4 mt-2">
            <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-600" />
              Bank Account Details
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bank Name
                </label>
                <input
                  type="text"
                  value={form.bankName}
                  onChange={(e) => setForm({ ...form, bankName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  placeholder="e.g., State Bank of India"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account Holder Name
                </label>
                <input
                  type="text"
                  value={form.accountHolderName}
                  onChange={(e) => setForm({ ...form, accountHolderName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  placeholder="As per bank records"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account Number
                </label>
                <input
                  type="text"
                  value={form.accountNumber}
                  onChange={(e) => setForm({ ...form, accountNumber: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  placeholder="Enter account number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  IFSC Code
                </label>
                <input
                  type="text"
                  value={form.ifscCode}
                  onChange={(e) => setForm({ ...form, ifscCode: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none uppercase"
                  placeholder="e.g., SBIN0001234"
                  maxLength="11"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  PAN Number
                </label>
                <input
                  type="text"
                  value={form.panNumber}
                  onChange={(e) => setForm({ ...form, panNumber: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none uppercase"
                  placeholder="e.g., ABCDE1234F"
                  maxLength="10"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-3 justify-end">
        <button
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={onSubmit}
          className="px-4 py-2 bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 transition-colors flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {submitLabel}
        </button>
      </div>
    </div>
  );
};

// Salary Form Component
const SalaryForm = ({ form, setForm, onSubmit, onCancel }) => {
  const basicSalary = parseFloat(form.basicSalary) || 0;
  const allowances = parseFloat(form.allowances) || 0;
  const deductions = parseFloat(form.deductions) || 0;
  const bonus = parseFloat(form.bonus) || 0;
  const netSalary = basicSalary + allowances + bonus - deductions;

  return (
    <div className="p-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <p className="text-sm text-blue-800">
          <strong>Staff:</strong> {form.staffName}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Month <span className="text-red-500">*</span>
          </label>
          <input
            type="month"
            value={form.month}
            onChange={(e) => setForm({ ...form, month: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Payment Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={form.paymentDate}
            onChange={(e) => setForm({ ...form, paymentDate: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Basic Salary (₹) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            value={form.basicSalary}
            onChange={(e) => setForm({ ...form, basicSalary: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
            placeholder="Enter basic salary"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Allowances (₹)
          </label>
          <input
            type="number"
            value={form.allowances}
            onChange={(e) => setForm({ ...form, allowances: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
            placeholder="Travel, food, etc."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Bonus (₹)
          </label>
          <input
            type="number"
            value={form.bonus}
            onChange={(e) => setForm({ ...form, bonus: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
            placeholder="Performance bonus"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Deductions (₹)
          </label>
          <input
            type="number"
            value={form.deductions}
            onChange={(e) => setForm({ ...form, deductions: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
            placeholder="Tax, advance, etc."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Payment Method
          </label>
          <select
            value={form.paymentMethod}
            onChange={(e) => setForm({ ...form, paymentMethod: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
          >
            <option value="bank_transfer">Bank Transfer</option>
            <option value="cash">Cash</option>
            <option value="cheque">Cheque</option>
            <option value="upi">UPI</option>
          </select>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows="2"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
            placeholder="Additional notes..."
          />
        </div>
      </div>

      {/* Salary Breakdown */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
        <h4 className="font-semibold text-gray-900 mb-3">Salary Breakdown</h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Basic Salary:</span>
            <span className="font-medium">₹{basicSalary.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Allowances:</span>
            <span className="font-medium text-green-600">+₹{allowances.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Bonus:</span>
            <span className="font-medium text-green-600">+₹{bonus.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Deductions:</span>
            <span className="font-medium text-red-600">-₹{deductions.toLocaleString()}</span>
          </div>
          <div className="border-t border-gray-300 pt-2 mt-2 flex justify-between">
            <span className="font-semibold text-gray-900">Net Salary:</span>
            <span className="font-bold text-lg text-gray-900">
              ₹{netSalary.toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      <div className="flex gap-3 justify-end">
        <button
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={onSubmit}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
        >
          <DollarSign className="w-4 h-4" />
          Record Payment
        </button>
      </div>
    </div>
  );
};

export default StaffManagement;
